import {
	useLocalStorageState,
	useUserScopedKey,
	useUserTimezone
} from "client/Hooks";
import { ReactElement, useContext, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { ZermeloLiveInformationProvider } from "./provider";
import { LiveTileContent } from "../LiveTileContent";
import { ZermeloDaySchedule } from "./ZermeloDaySchedule";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ZermeloWeekSchedule } from "./ZermeloWeekSchedule";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

enum ScheduleKind {
	Day = 0,
	Week = 1
}

export type ZermeloLiveTileContentProps = {
	connectionId: number;
	destinationWindow: DestinationWindow;
	provider: ZermeloLiveInformationProvider;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ZermeloLiveTileContent = (props: ZermeloLiveTileContentProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		provider,
		onOpenTileMenu
	} = props;

	const [activeDate, setActiveDate] = useState<Date>(new Date());
	const userScopedKey: string = useUserScopedKey("zermelo-live-tile-content:preferred-schedule-view");
	const [preferredScheduleKind, setPreferredScheduleKind] = useLocalStorageState<ScheduleKind>(userScopedKey, ScheduleKind.Week);

	const {
		icon,
		name,
		environmentAppId,
		dimension: dimensionToRender,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension: dimensionToRender,
		appIsLicensed,
		appIsNotAccessible
	});

	const userTimezone: TimeZone = useUserTimezone();

	const isWeekScheduleAvailable: boolean = dimensionToRender.dimension.columnSpan >= 6;
	const showTodayButton: boolean = dimensionToRender.dimension.columnSpan === 8;

	const handleNavigation = (): void => {
		addAnalyticsEvent();
		navigateRelativeToEnvironment(
			`/Workspace/LiveInformation/Zermelo/${connectionId}/GoToSchedule`,
			destinationWindow === DestinationWindow.NewWindow
		);
	};

	const renderSchedule = (): ReactElement => {
		const scheduleKind: ScheduleKind = isWeekScheduleAvailable
			? preferredScheduleKind
			: ScheduleKind.Day;

		switch (scheduleKind) {
			case ScheduleKind.Day:
				return (
					<ZermeloDaySchedule
						destinationWindow={destinationWindow}
						connectionId={connectionId}
						provider={provider}
						date={activeDate}
						onDateChange={setActiveDate}
						isScheduleSelectionAvailable={isWeekScheduleAvailable}
						onChangeScheduleKind={setPreferredScheduleKind}
						isTodayButtonAvailable={showTodayButton}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case ScheduleKind.Week:
				return (
					<ZermeloWeekSchedule
						destinationWindow={destinationWindow}
						connectionId={connectionId}
						provider={provider}
						date={activeDate}
						onDateChange={setActiveDate}
						onChangeScheduleKind={setPreferredScheduleKind}
						isTodayButtonAvailable={showTodayButton}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			default:
				throw new UnreachableCaseError(scheduleKind);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.Title text={localize("_MySchedule")} />
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{ renderSchedule() }
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_MySchedule")}
				onClick={handleNavigation}
			/>
		</LiveTileContent>
	);
};
ZermeloLiveTileContent.displayName = "ZermeloLiveTileContent";
